
import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { State, Action, Getter, Mutation } from 'vuex-class';
import HttpUtils from '../assets/utils/HttpRequest';
import Field from '../assets/interfaces/Field';
import ToolbarAction from '../assets/interfaces/ToolbarAction';
import HttpRequest from '../assets/utils/HttpRequest';
import Util from '../assets/utils/Util';

import TkForm from './TkForm.vue';

@Component({
  name: 'TkNavigableForm',
  components: {
    TkForm,
  },
})
class TkNavigableForm extends Vue {
  // #region [ PROPS ]
  @Prop({ default: () => [] })
  private actions: (ToolbarAction | string)[];

  @Prop({ default: () => new Object() })
  private default: any;

  @Prop({ default: () => new Object() })
  private fields: Field[];

  @Prop({ default: () => new Object() })
  private filters: any;

  @Prop({ default: 0 })
  private index: number;

  @Prop({ default: false })
  private loading: boolean;

  @Prop({ default: 'view' })
  private mode: string;

  @Prop({ default: '' })
  private primaryKey: string;

  @Prop({ default: '' })
  private route: string;

  @Prop({ default: () => [] })
  private rows: any[];

  @Prop({ default: 0 })
  private triggerAdd: number;

  @Prop({ default: 0 })
  private triggerCancel: number;

  @Prop({ default: 0 })
  private triggerReload: number;
  // #endregion

  // #region [ EVENTS ]
  @Emit('after-reload')
  private emitAfterReload(rows: any[]) {}

  @Emit('error')
  private emitError(error: Error) {}

  @Emit('index-change')
  private emitIndexChange(index: number, row: any) {
    return [index, row];
  }

  @Emit('input')
  private emitInput(row: any, rows: any[]) {
    return [row, rows];
  }

  @Emit('new-row')
  private emitNewRow(index: number) {}

  @Emit('update:loading')
  private emitUpdateLoading(isLoading: boolean) {}
  // #endregion

  // #region [ DATA ]
  private internalRows = [];

  private internalIndex = 0;

  private internalTriggerReload = 0;

  private internalLoading = false;

  private backup = null;

  private defaultActions = [
    {
      name: 'reload',
      label: 'Atualizar',
      icon: 'mdi-refresh',
      showLabel: false,
      onClick: this.reload,
    },
    {
      name: 'add',
      label: 'Adicionar',
      icon: 'mdi-plus-circle',
      showLabel: true,
      onClick: this.add,
    },
    {
      name: 'cancel',
      label: 'Cancelar',
      icon: 'undo',
      showLabel: true,
      onClick: this.cancel,
    },
  ];

  private cancelRequest = false;
  // #endregion

  // #region [ COMPUTED ]
  private get navText(): string {
    const totalRows = this.internalRows.length;

    const currentIndex = totalRows > 0 ?
      this.internalIndex + 1 :
      0;

    const text = `${currentIndex} de ${totalRows}`;

    return text;
  }

  private get inFirstItem(): boolean {
    const inFirst = this.internalIndex === 0;

    return inFirst;
  }

  private get inLastItem(): boolean {
    const inLast = this.internalIndex >= this.internalRows.length - 1;

    return inLast;
  }

  private get currentRow(): any {
    const row =
      this.internalRows[this.internalIndex] ||
      null;

    return row;
  }

  private get toolbarActions(): (ToolbarAction | string)[] {
    const actions = this.actions.map((action) => {
      let tbAction;

      if (typeof action === 'string') {
        tbAction =
          this.findDefaultAction(action) ||
          action;
      } else {
        tbAction = action;
      }

      return tbAction;
    });

    return actions;
  }

  private get isLoading(): boolean {
    const loading = this.loading || this.internalLoading;

    return loading;
  }

  private get formMode(): string {
    const mode = this.internalRows.length === 0 ?
      'view' :
      this.mode;

    return mode;
  }

  private get currentRowIsNew(): boolean {
    if (!this.currentRow) {
      return true;
    }

    const isNew = this.currentRow.__is_new === true;

    return isNew;
  }
  // #endregion

  // #region [ WATCHERS ]
  @Watch('index', { immediate: true })
  private indexOnChange() {
    this.internalIndex = this.index;
    this.backup = Util.deepCopy(this.currentRow);
  }

  @Watch('rows', { immediate: true, deep: true })
  private rowsOnChange() {
    this.internalRows = Util.deepCopy(this.rows);
    this.backup = Util.deepCopy(this.internalRows);
  }

  @Watch('triggerAdd')
  private triggerAddChange() {
    this.add();
  }

  @Watch('triggerCancel')
  private triggerCancelChange() {
    this.cancel();
  }

  @Watch('triggerReload')
  private triggerReloadChange() {
    this.reload();
  }
  // #endregion

  // #region [ METHODS ]
  private previousButtonOnClick() {
    this.internalIndex--;
    this.backup = Util.deepCopy(this.currentRow);
    this.emitIndexChange(this.internalIndex, this.currentRow);
  }

  private nextButtonOnClick() {
    this.internalIndex++;
    this.backup = Util.deepCopy(this.currentRow);
    this.emitIndexChange(this.internalIndex, this.currentRow);
  }

  private firstButtonOnClick() {
    this.internalIndex = 0;

    this.backup = Util.deepCopy(this.currentRow);
    this.emitIndexChange(this.internalIndex, this.currentRow);
  }

  private lastButtonOnClick() {
    this.internalIndex = this.internalRows.length === 0 ?
      0 :
      this.internalRows.length - 1;

    this.backup = Util.deepCopy(this.currentRow);
    this.emitIndexChange(this.internalIndex, this.currentRow);
  }

  private async reload() {
    if (!this.route) {
      return;
    }

    const params = {
      requestType: 'FilterData',
      ...this.filters,
    };

    const isNew = this.currentRow ?
      this.currentRow.__is_new :
      false;

    const pkValue = this.primaryKey && this.currentRow ?
      this.currentRow[this.primaryKey] :
      null;

    this.internalLoading = true;
    this.emitUpdateLoading(this.internalLoading);

    try {
      const response = await HttpRequest.get(this.route, params, false);

      const rows =
        response.data.dataset.data ||
        response.data.dataset[this.route];

      this.internalRows = rows;
      this.backup = Util.deepCopy(this.currentRow);

      let newIndex = this.getIndexByPrimaryKey(pkValue);

      if (newIndex === -1) {
        newIndex = 0;
      }

      this.internalIndex = newIndex;
      this.emitIndexChange(this.internalIndex, this.currentRow);

      this.emitAfterReload(this.internalRows);
    } catch (err) {
      this.emitError(err);
    }

    this.internalLoading = false;
    this.emitUpdateLoading(this.internalLoading);
  }

  private findDefaultAction(actionName: string): ToolbarAction {
    let action = this.defaultActions.find((defaultAction) => {
      const isAction = defaultAction.name === actionName;

      return isAction;
    });

    return action;
  }

  private add() {
    const newRow = Util.deepCopy(this.default);

    newRow.__is_new = true;

    this.internalRows.push(newRow);
    this.internalIndex = this.internalRows.length - 1;

    this.emitNewRow(this.internalIndex);
    this.emitIndexChange(this.internalIndex, this.currentRow);
  }

  private cancel() {
    if (this.currentRow.__is_new) {
      this.internalRows.splice(this.internalIndex, 1);
      this.updateIndex();
      this.emitIndexChange(this.internalIndex, this.currentRow);
    } else {
      this.internalRows[this.internalIndex] = Util.deepCopy(this.backup);
      this.emitInput(this.currentRow, this.internalRows);
    }
  }

  private updateIndex() {
    const lastIndex = this.internalRows.length - 1;
    const newIndex = Math.max(lastIndex, 0);

    this.internalIndex = newIndex;
  }

  private onInput(row: any) {
    if (this.internalRows.length > 0) {
      this.internalRows[this.internalIndex] = row;
      this.emitInput(row, this.internalRows);
    }
  }

  private getIndexByPrimaryKey(pkValue: string | number): number {
    if (!this.primaryKey) {
      return -1;
    }

    const index: number = this.internalRows.reduce((accumulator: number, row: any, i: number) => {
      const same = row[this.primaryKey] === pkValue;
      const rightIndex = same ?
        i :
        accumulator;

      return rightIndex;
    }, -1);

    return index;
  }
  // #endregion
}

export default TkNavigableForm;
